import React, { useState, useContext } from 'react';
import { X, Send, AlertCircle, Mail } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { sendAlertToAllUsers } from '../services/programService';

const AlertModal = ({ isOpen, onClose, onSuccess }) => {
  const { currentUser, userRole } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    sendEmail: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const isAdmin = ['master_admin', 'overseer', 'colony_prime_minister'].includes(userRole);

  if (!isAdmin) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <AlertCircle size={24} />
            <span>Only admins can send alerts</span>
          </div>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.title.trim() || !formData.message.trim()) {
        setError('Title and message are required');
        setLoading(false);
        return;
      }

      const alertData = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        sendEmail: formData.sendEmail,
        createdBy: currentUser.uid,
      };

      await sendAlertToAllUsers(alertData);
      onSuccess();
      setFormData({
        title: '',
        message: '',
        type: 'info',
        sendEmail: false,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to send alert');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Send Alert to All Users</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Alert Title */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Alert Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter alert title"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Alert Message */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Message *
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Enter alert message"
              rows="5"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              disabled={loading}
            />
          </div>

          {/* Alert Type */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Alert Type
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="info">ℹ️ Info</option>
              <option value="success">✅ Success</option>
              <option value="warning">⚠️ Warning</option>
              <option value="urgent">🔴 Urgent</option>
            </select>
          </div>

          {/* Email Option */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="sendEmail"
                checked={formData.sendEmail}
                onChange={handleCheckboxChange}
                className="w-5 h-5 border border-gray-300 rounded"
                disabled={loading}
              />
              <div className="flex items-center gap-2">
                <Mail size={18} className="text-blue-600" />
                <span className="text-sm font-medium text-gray-700">
                  Send email notification to all users
                </span>
              </div>
            </label>
            <p className="text-xs text-gray-600 mt-2 ml-8">
              Users will receive both in-app and email notifications
            </p>
          </div>

          {/* Info Box */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <p>
              ⚠️ This alert will be sent to <strong>all active users</strong> in the system. Please review the content carefully before sending.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              disabled={loading}
            >
              <Send size={18} />
              {loading ? 'Sending...' : 'Send Alert'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AlertModal;
